import { getNonce } from './nonce';
import { calculateCSPHash } from './csp-health-check';
import { logger } from './logger';

// Detect if we're on a mobile device
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

/**
 * Load a script securely with nonce
 * @param url The URL of the script to load
 * @param attributes Optional attributes to add to the script tag
 * @returns A promise that resolves when the script is loaded
 */
export const loadScriptSecurely = (url: string, attributes: Record<string, string> = {}): Promise<HTMLScriptElement> => {
  return new Promise((resolve, reject) => {
    // Don't load the same script twice
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      resolve(existingScript as HTMLScriptElement);
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    
    // Add CSP nonce if available
    const nonce = getNonce();
    if (nonce) {
      script.nonce = nonce;
    }
    
    // Add any additional attributes
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    
    // Handle loading events
    script.onload = () => resolve(script);
    script.onerror = (error) => {
      logger.warn(`Failed to load script: ${url}`, error);
      // On mobile, we'll still resolve to prevent blocking the app
      if (isMobile) {
        logger.debug('Mobile device detected, continuing despite script load error');
        resolve(script);
      } else {
        reject(new Error(`Failed to load script: ${url}`));
      }
    };
    
    // Append to document
    document.head.appendChild(script);
  });
};

/**
 * Execute inline script securely with proper nonce and CSP hash
 * @param code The JavaScript code to execute
 * @param attributes Optional attributes to add to the script tag
 */
export const executeScriptSecurely = async (code: string, attributes: Record<string, string> = {}): Promise<void> => {
  // Don't execute empty code or HTML as script content (security protection)
  if (!code || code.trim() === '' || code.includes('<!DOCTYPE') || code.includes('<html')) {
    logger.warn('Attempted to execute empty code or HTML as script');
    return;
  }
  
  try {
    // Calculate hash for the inline script
    const hash = await calculateCSPHash(code);
    
    const script = document.createElement('script');
    script.textContent = code;
    
    // Add CSP nonce if available
    const nonce = getNonce();
    if (nonce) {
      script.nonce = nonce;
    }
    
    // Add any additional attributes
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    
    // Append to document
    document.head.appendChild(script);
    
    // Remove the script element after execution
    setTimeout(() => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }, 10);
  } catch (error) {
    logger.error('Error executing script securely:', error);
    
    // On mobile, we'll try a fallback approach
    if (isMobile) {
      logger.debug('Mobile device detected, trying fallback script execution');
      try {
        // Fallback: use Function constructor (less secure but may work on mobile)
        const fallbackFn = new Function(code);
        fallbackFn();
      } catch (fallbackError) {
        logger.error('Fallback script execution also failed:', fallbackError);
      }
    }
  }
};

/**
 * Fix script loading issues specifically for MdbApp scripts
 * This is a specialized function for MdbApp-specific script loading
 */
export const fixMdbAppScriptLoading = async (): Promise<void> => {
  try {
    logger.debug('Fixing MdbApp script loading...');
    
    // List of essential scripts that need to be loaded with proper nonce
    const essentialScripts = [
      'https://www.googletagmanager.com/gtag/js',
      'https://mdbapp.io/scripts/web3-connect.js',
      'https://mdbapp.io/scripts/token-verification.js'
    ];
    
    // Find all scripts without nonce that match our essential patterns
    const scripts = document.querySelectorAll('script');
    const scriptsToFix = Array.from(scripts).filter(script => {
      const src = script.getAttribute('src') || '';
      return !script.hasAttribute('nonce') && 
        essentialScripts.some(pattern => src.includes(pattern));
    });
    
    // Remove problematic scripts and reload them with proper nonce
    for (const script of scriptsToFix) {
      const src = script.getAttribute('src');
      if (src) {
        logger.debug(`Fixing script: ${src}`);
        script.remove();
        await loadScriptSecurely(src);
      }
    }
    
    logger.debug('Script loading fixed successfully');
  } catch (error) {
    logger.error('Error fixing script loading:', error);
  }
};

/**
 * Special handling for mobile wallet connections
 * This function adds specific fixes for mobile wallet issues
 */
export const fixMobileWalletConnections = (): void => {
  if (!isMobile) return;
  
  logger.debug('Applying mobile wallet connection fixes');
  
  try {
    // 1. Ensure WalletConnect scripts are properly loaded
    const wcScripts = [
      'https://unpkg.com/@walletconnect/web3-provider',
      'https://unpkg.com/@walletconnect/qrcode-modal'
    ];
    
    wcScripts.forEach(src => {
      if (!document.querySelector(`script[src="${src}"]`)) {
        loadScriptSecurely(src)
          .then(() => logger.debug(`Successfully loaded ${src}`))
          .catch(err => logger.warn(`Failed to load ${src}`, err));
      }
    });
    
    // 2. Add special handling for deep links
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const walletLink = target.closest('a[href^="https://metamask.app.link"], a[href^="trust://"], a[href^="https://link.trustwallet.com"]');
      
      if (walletLink) {
        e.preventDefault();
        const href = walletLink.getAttribute('href');
        if (href) {
          // Open in a new tab to avoid COOP restrictions
          window.open(href, '_blank', 'noopener');
        }
      }
    }, { capture: true });
    
    // 3. Add a global error handler for WalletConnect
    window.addEventListener('error', (event) => {
      if (event.message && (
        event.message.includes('walletconnect') || 
        event.message.includes('web3modal') ||
        event.message.includes('wallet')
      )) {
        logger.warn('Intercepted wallet-related error:', event.message);
        // Prevent the error from bubbling up
        event.preventDefault();
        event.stopPropagation();
      }
    }, { capture: true });
    
    logger.debug('Mobile wallet connection fixes applied');
  } catch (error) {
    logger.error('Error applying mobile wallet fixes:', error);
  }
};

/**
 * Enhanced network switching handler with special Sepolia handling
 */
export const handleNetworkSwitching = async (
  chainId: number | string,
  provider: any,
  connector: any
) => {
  logger.debug('handleNetworkSwitching called with chainId:', chainId);
  
  // Convert chainId to number if it's a hex string
  const chainIdNum = typeof chainId === 'string' 
    ? parseInt(chainId, 16) 
    : chainId;
  
  logger.debug('Normalized chainId:', chainIdNum);
  
  // Special handling for Sepolia
  if (chainIdNum === 11155111) {
    logger.debug('Detected Sepolia network change');
    
    // Store the network change in localStorage for persistence across refreshes
    localStorage.setItem('lastNetworkChange', JSON.stringify({
      chainId: chainIdNum,
      timestamp: Date.now()
    }));
    
    // Track reconnection attempts to prevent infinite loops
    const reconnectAttempts = parseInt(localStorage.getItem('sepoliaReconnectAttempts') || '0');
    
    // Only attempt reconnection if we haven't tried too many times
    if (reconnectAttempts < 3) {
      localStorage.setItem('sepoliaReconnectAttempts', (reconnectAttempts + 1).toString());
      
      logger.debug(`Sepolia reconnect attempt ${reconnectAttempts + 1}/3`);
      
      // Two-step reconnection process for Sepolia
      try {
        // Step 1: Disconnect current connection
        if (connector?.disconnect) {
          logger.debug('Disconnecting current connection');
          await connector.disconnect();
        }
        
        // Small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Step 2: Reconnect with the new chain
        if (connector?.connect) {
          logger.debug('Reconnecting with Sepolia chain');
          await connector.connect({ chainId: chainIdNum });
          
          logger.debug('Sepolia reconnection successful');
          
          // Reset reconnection attempts on success
          localStorage.setItem('sepoliaReconnectAttempts', '0');
          
          // Force refresh to ensure UI updates correctly
          logger.debug('Refreshing page to reflect Sepolia network change');
          window.location.reload();
          return true;
        }
      } catch (error) {
        logger.error('Error during Sepolia reconnection:', error);
        
        // If we've tried multiple times and failed, force a refresh
        if (reconnectAttempts >= 2) {
          logger.debug('Multiple Sepolia reconnection attempts failed, forcing refresh');
          window.location.reload();
          return false;
        }
      }
    } else {
      logger.debug('Too many Sepolia reconnection attempts, resetting counter');
      localStorage.setItem('sepoliaReconnectAttempts', '0');
      
      // Force refresh as a last resort
      window.location.reload();
      return false;
    }
  }
  
  // Standard handling for other networks
  try {
    if (connector?.connect) {
      await connector.connect({ chainId: chainIdNum });
      return true;
    }
  } catch (error) {
    logger.error('Error switching network:', error);
    return false;
  }
  
  return false;
};

// Automatically apply mobile fixes if on a mobile device
if (isMobile) {
  // Wait for DOM to be fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      fixMobileWalletConnections();
      handleNetworkSwitching(1, null, null); // Call handleNetworkSwitching with default values
    });
  } else {
    fixMobileWalletConnections();
    handleNetworkSwitching(1, null, null); // Call handleNetworkSwitching with default values
  }
} else {
  // For desktop, just set up network switching
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      handleNetworkSwitching(1, null, null); // Call handleNetworkSwitching with default values
    });
  } else {
    handleNetworkSwitching(1, null, null); // Call handleNetworkSwitching with default values
  }
}

// Add window.ethereum type definition for TypeScript
declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      chainId?: string;
      on: (event: string, callback: any) => void;
      removeListener: (event: string, callback: any) => void;
      request: (args: { method: string; params?: any[] }) => Promise<any>;
    };
  }
}
